import React from 'react'
import { find } from 'lodash'
import { Grid, Typography, useMediaQuery } from '@material-ui/core'
import { IKnowledgeProps } from './Interface'
import useStyles, { Container, AddCircleIcon, DeleteIcon, RatingSlider } from './Styles'
import { Input } from '../../components/input'
import { Balloon, Corner } from '../balloon'
import classNames from 'classnames/bind'
import { theme } from 'config/constants/theme/theme'
import { TAG_MARKERS } from 'smart-components/im-good-at/store/State'
import handleEnterKey from 'utils/handle-enter-key'

export const Knowledge: React.FC<IKnowledgeProps> = ({
  isInEditMode,
  initialValues,
  color,
  rules,
  onAddTag,
  onRemoveTag,
  onAddExpertise,
  onEditExpertise,
  onTagLevelChange,
  onTagTitleChange,
  onTitleChange,
  onDeleteExpertise,
  onSubmitExpertise,
  error
}) => {
  const getValue = (v: number) => TAG_MARKERS.find(({ value }) => v <= value).label
  const classes = useStyles({})
  const { title, tags } = initialValues || { title: '', tags: {} }
  const numberOfTags = Object.keys(tags).length
  const downSM = useMediaQuery(theme.breakpoints.down('sm'))

  const maxRatings = 3

  const onSubmitHandler = () => {
    if (!error) {
      onSubmitExpertise()
    }
  }

  const handleSliderChange = (evt: any, key: number, value: number) => {
    evt.preventDefault()
    evt.stopPropagation()
    onTagLevelChange(key, value)
  }

  const renderFillMessage = () => {
    if (!error) {
      return null
    }

    const tagsAsArray = Object.entries(tags).map(([, tag]) => tag)

    const defaultMSg = 'You should fill title and at least one tag in order to get things saved as expected!'

    const invalidTagsLength =
      tagsAsArray.length &&
      Boolean(find(tagsAsArray, tag => tag?.title?.length < rules?.item?.tags?.item?.title?.minLength))

    const invalidTagsLengthMsg = `Tags must be at least ${rules?.item?.tags?.item?.title?.minLength} characters!`

    const msg = invalidTagsLength
      ? invalidTagsLengthMsg
      : !title || !tagsAsArray.length
      ? defaultMSg
      : 'Please fill out the level of expertise to fill out save your response'
    return (
      <Typography>
        <label id="Knowledge-error-msg-label" className={classes.error}>
          {msg}
        </label>
      </Typography>
    )
  }

  const renderTitle = () => {
    return isInEditMode ? (
      <div className={classes.header}>
        <Typography>
          <label id="Knowledge-label-title" className={classes.labelTitle}>
            What expertise do you bring to the table?
          </label>
        </Typography>
        <Grid container direction={'row'}>
          <Grid item xs={11}>
            <Input
              id="knowledge-title-label"
              initialValue={title}
              onKeyPress={handleEnterKey()}
              onChanged={onTitleChange}
              limit={rules?.item?.title?.maxLength}
              showLimit={true}
              placeholder={'Synthesizing Information'}
            />
          </Grid>
          <Grid item xs={1}>
            <DeleteIcon
              className={classes.floatRight}
              id="knowledge-remove-title-button"
              onClick={(evt: any) => {
                evt.preventDefault()
                evt.stopPropagation()
                onDeleteExpertise()
              }}
            />
          </Grid>
        </Grid>
      </div>
    ) : (
      <div className={classes.headerKnowledge}>
        <Typography>
          <label id="knowledge-title-label" className={classes.title}>
            {title}
          </label>
        </Typography>
      </div>
    )
  }

  const renderRemoveButton = (key: number) => {
    const hide = classNames.bind(classes)({ hide: numberOfTags <= 1 })
    return isInEditMode ? (
      <div className={hide}>
        <DeleteIcon
          id={`${key}-knowledge-remove-tag-button`}
          onClick={(evt: any) => {
            evt.preventDefault()
            evt.stopPropagation()
            onRemoveTag(key)
          }}
        />
      </div>
    ) : null
  }

  const renderAddRating = () => {
    return isInEditMode && numberOfTags < maxRatings ? (
      <div className={classes.row}>
        <div>
          <AddCircleIcon
            id={'knowledge-add-rating-button'}
            fontSize="large"
            onClick={(evt: any) => {
              evt.preventDefault()
              evt.stopPropagation()
              onAddTag()
            }}
          />
        </div>
      </div>
    ) : null
  }

  const renderRatingSlider = (key: number, item: any) => {
    const level: any = item.level || 0

    const props: any = {}
    if (isInEditMode) {
      props.marks = true
      props.className =
        !level || level < 2 ? classes.marksEmpty : level < 3 ? classes.marksLevelThree : classes.marksFull
    }

    return (
      <Grid className={classes.gridExpertise} item xs={5}>
        <Typography id={`knowledge-rating-slider-${key}`} className={classes.expertise}>
          {getValue(item.level || 0)}
        </Typography>
        <RatingSlider
          {...(isInEditMode ? { marks: true } : {})}
          className={!level || level < 2 ? classes.marksEmpty : level < 3 ? classes.marksLevelThree : classes.marksFull}
          valueLabelDisplay="off"
          disabled={!isInEditMode}
          min={0}
          max={4}
          value={level}
          onChange={(evt: any, value: any) => handleSliderChange(evt, key, value)}
        />
      </Grid>
    )
  }

  const renderTagTitle = (key: number, item: any, submit: boolean) => {
    return isInEditMode ? (
      <Input
        id={`${key}-knowledge-tag-title-answer`}
        showLimit
        onKeyPress={handleEnterKey(submit ? onSubmitHandler : undefined)}
        limit={rules?.item?.tags?.item?.title?.maxLength}
        placeholder="DecipheringDetails"
        initialValue={item.title}
        onChanged={value => onTagTitleChange(key, value)}
      />
    ) : (
      <Typography className={classes.paragraph}>
        <label id={`${key}-knowledge-tag-title-label`} className={classes.definedCategory} title={item.title}>
          {item.title ? item.title : ''}
        </label>
      </Typography>
    )
  }

  const renderComponent = (key: number, item: any, submit: boolean) => {
    return (
      <Grid item key={key} className={classes.row}>
        <Grid container direction="row" spacing={2} justify="space-around" alignItems="center">
          {renderRatingSlider(key, item)}
          <Grid item xs={7} style={{ whiteSpace: 'nowrap' }}>
            {renderTagTitle(key, item, submit)}
          </Grid>
        </Grid>
        {renderRemoveButton(key)}
      </Grid>
    )
  }

  return (
    <Container
      container
      onClick={(evt: any) => {
        evt.preventDefault()
        evt.stopPropagation()
        if (initialValues) {
          onEditExpertise()
        } else {
          onAddExpertise()
        }
      }}
    >
      <Grid item md={2} className={classes.balloon} hidden={downSM}>
        <Balloon corner={Corner.MiddleRight} bgColor={color} size={38} shadow={false} />
      </Grid>
      {initialValues && (
        <Grid item md={10} xs={12} className={classes.gridContainer}>
          {renderFillMessage()}
          {renderTitle()}
          <Grid item className={classes.ratingContainer}>
            <Grid container direction="column" spacing={1} justify="center">
              {Object.entries(tags).map(([key, item], index) => {
                return renderComponent(parseInt(key, 10), item, Object.entries(tags).length === index + 1)
              })}
              {renderAddRating()}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Container>
  )
}

Knowledge.defaultProps = {
  color: '#1BA3E6',
  error: false
}
